import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/use-auth';
import { eirsaliyeService } from '@/lib/eirsaliye-service';
import { useToast } from '@/hooks/use-toast';

export function useEmailConnections() {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ['email-connections', companyId],
    queryFn: () => eirsaliyeService.getConnectionStatus(companyId),
    enabled: !!companyId,
    staleTime: 10000, // Avoid duplicate fetches within 10s (React StrictMode)
    refetchInterval: 30000, // Refresh every 30s
    retry: 1,
  });
}

export function useEirsaliyes(options?: {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
}) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ['eirsaliyes', companyId, options],
    queryFn: () => eirsaliyeService.getEirsaliyes(companyId, options),
    enabled: !!companyId,
  });
}

export function useEirsaliyeDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['eirsaliye-detail', id],
    queryFn: () => eirsaliyeService.getEirsaliyeById(id!),
    enabled: !!id,
  });
}

export function useEirsaliyeStats() {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ['eirsaliye-stats', companyId],
    queryFn: () => eirsaliyeService.getStats(companyId),
    enabled: !!companyId,
  });
}

export function useDailyTotals(date: string) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ['eirsaliye-daily-totals', companyId, date],
    queryFn: () => eirsaliyeService.getDailyTotals(companyId, date),
    enabled: !!companyId && !!date,
  });
}

export function useDailyItems(date: string) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ['eirsaliye-daily-items', companyId, date],
    queryFn: () => eirsaliyeService.getDailyItems(companyId, date),
    enabled: !!companyId && !!date,
  });
}

export function useDailyIrsaliyes(date: string) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ['eirsaliye-daily-list', companyId, date],
    queryFn: () => eirsaliyeService.getDailyIrsaliyes(companyId, date),
    enabled: !!companyId && !!date,
  });
}

export function useSyncLogs() {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ['eirsaliye-sync-logs', companyId],
    queryFn: () => eirsaliyeService.getSyncLogs(companyId),
    enabled: !!companyId,
  });
}

export function useTriggerSync() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { companyId } = useAuth();

  return useMutation({
    mutationFn: (connectionId?: string) => eirsaliyeService.triggerSync(companyId, connectionId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['eirsaliyes'] });
      queryClient.invalidateQueries({ queryKey: ['eirsaliye-stats'] });
      queryClient.invalidateQueries({ queryKey: ['eirsaliye-sync-logs'] });
      queryClient.invalidateQueries({ queryKey: ['email-connections'] });
      queryClient.invalidateQueries({ queryKey: ['eirsaliye-daily-totals'] });
      queryClient.invalidateQueries({ queryKey: ['eirsaliye-daily-items'] });

      // Check for per-provider errors in the response
      const errors = data.results?.filter((r: any) => r.error || r.status === 'failed') || [];
      const total = data.results?.reduce((sum: number, r: any) => sum + (r.irsaliyes_created || 0), 0) || 0;

      if (errors.length > 0) {
        const errorMessages = errors.map((r: any) => `${r.provider}: ${r.error || 'Bilinmeyen hata'}`).join('\n');
        toast({
          title: 'Senkronizasyon Hatasi',
          description: errorMessages,
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Senkronizasyon Tamamlandi',
          description: total > 0
            ? `${total} yeni e-Irsaliye islendi.`
            : 'Yeni e-Irsaliye bulunamadi.',
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: 'Senkronizasyon Hatasi',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useDisconnectProvider() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { companyId } = useAuth();

  return useMutation({
    mutationFn: (provider: 'gmail' | 'outlook') => eirsaliyeService.disconnectProvider(companyId, provider),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-connections'] });
      toast({ title: 'Baglanti Kesildi' });
    },
    onError: (error: Error) => {
      toast({ title: 'Hata', description: error.message, variant: 'destructive' });
    },
  });
}

export function useRetryEmail() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (emailId: string) => eirsaliyeService.retryEmail(emailId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eirsaliyes'] });
      queryClient.invalidateQueries({ queryKey: ['eirsaliye-stats'] });
      toast({ title: 'Yeniden isleme basarili' });
    },
    onError: (error: Error) => {
      toast({ title: 'Yeniden isleme hatasi', description: error.message, variant: 'destructive' });
    },
  });
}

export function useUpdateStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      eirsaliyeService.updateStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eirsaliyes'] });
      queryClient.invalidateQueries({ queryKey: ['eirsaliye-detail'] });
      queryClient.invalidateQueries({ queryKey: ['eirsaliye-stats'] });
    },
  });
}

export function useMarkItemsManuallyReceived() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { companyId } = useAuth();

  return useMutation({
    mutationFn: async (itemIds: string[]) => {
      if (!companyId) throw new Error('Sirket bilgisi bulunamadi');
      return eirsaliyeService.markItemsManuallyReceived(companyId, itemIds);
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ['eirsaliyes'] });
      queryClient.invalidateQueries({ queryKey: ['eirsaliye-detail'] });
      queryClient.invalidateQueries({ queryKey: ['eirsaliye-daily-items'] });
      queryClient.invalidateQueries({ queryKey: ['eirsaliye-daily-list'] });
      queryClient.invalidateQueries({ queryKey: ['eirsaliye-daily-totals'] });
      queryClient.invalidateQueries({ queryKey: ['eirsaliye-stats'] });

      toast({
        title: 'Kalemler manuel alindi',
        description: `${count} kalem stok degisikligi olmadan kapatildi.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Manuel kapatma hatasi',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
